import { useState } from "react";
import { MapPin, Clock, Phone, MessageCircle, Accessibility, Car, Siren } from "lucide-react";
import { Button } from "../ui/button";
import { CLINIC, telLink, waLink } from "../../lib/clinic";

export const Ubicacion = () => {
  const [active, setActive] = useState(0);
  const current = CLINIC.consultorios[active];
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(current.mapsQuery)}&output=embed`;

  return (
    <section id="ubicacion" data-testid="ubicacion-section" className="py-20 md:py-28 bg-[hsl(var(--cream))] relative">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-3xl mb-10">
          <span className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--soft-red))] font-medium">
            · Ubicación y citas ·
          </span>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[hsl(var(--navy))] leading-[1.1]">
            Dos consultorios,
            <br />
            <span className="italic font-light">una sola atención.</span>
          </h2>
          <p className="mt-4 text-[hsl(var(--navy-soft))] text-base md:text-lg">
            Elige el consultorio más cómodo para ti — atendemos en ambas sedes con la misma calidad y equipo.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-[hsl(var(--border))]" data-testid="consultorio-tabs">
          {CLINIC.consultorios.map((c, i) => (
            <button
              key={c.id}
              onClick={() => setActive(i)}
              data-testid={`consultorio-tab-${i}`}
              className={`relative px-5 py-3 text-sm font-medium transition-colors -mb-px ${
                active === i
                  ? "text-[hsl(var(--navy))] border-b-2 border-[hsl(var(--soft-red))]"
                  : "text-[hsl(var(--navy-soft))] hover:text-[hsl(var(--navy))]"
              }`}
            >
              <span className="block text-[10px] uppercase tracking-[0.2em] opacity-60 mb-0.5">{c.label}</span>
              <span className="font-serif-display text-base">{c.name}</span>
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Info column */}
          <div className="lg:col-span-5">
            <div className="space-y-5">
              <Row icon={MapPin} title="Dirección">
                <p className="text-[hsl(var(--navy))] font-medium" data-testid={`consultorio-${active}-address1`}>
                  {current.addressLine1}
                </p>
                <p className="text-sm text-[hsl(var(--navy-soft))]">{current.addressLine2}</p>
                <p className="text-sm text-[hsl(var(--navy-soft))]">{current.city}</p>
              </Row>

              <Row icon={Clock} title="Horarios de consulta">
                <ul className="space-y-2.5" data-testid={`consultorio-${active}-schedule`}>
                  {current.schedule.map((s, idx) => (
                    <li key={idx} className="text-sm">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="font-medium text-[hsl(var(--navy))]">{s.label}</span>
                        <span className="font-medium text-[hsl(var(--navy))]">{s.time}</span>
                      </div>
                      <div className="text-xs text-[hsl(var(--navy-soft))] mt-0.5">{s.days}</div>
                    </li>
                  ))}
                </ul>
              </Row>

              <Row icon={Accessibility} title="Accesibilidad">
                <p className="text-sm text-[hsl(var(--navy-soft))] leading-relaxed">
                  Consultorios totalmente equipados y accesibles para personas con
                  <strong className="text-[hsl(var(--navy))]"> capacidades diferentes o movilidad reducida</strong>.
                  Contamos con servicio de <em>cardiología a domicilio</em>.
                </p>
              </Row>

              <Row icon={Car} title="Estacionamiento">
                <p className="text-sm text-[hsl(var(--navy-soft))]">
                  Estacionamiento amplio disponible en ambas sedes.
                </p>
              </Row>

              <Row icon={Siren} title="Línea de urgencias 24/7">
                <a
                  href={`tel:${CLINIC.emergencyNumber}`}
                  className="text-[hsl(var(--soft-red))] font-medium hover:underline"
                  data-testid="ubicacion-emergency-link"
                >
                  {CLINIC.emergencyDisplay}
                </a>
                <p className="text-xs text-[hsl(var(--navy-soft))] mt-1">
                  Para dolor opresivo en pecho, falta de aire súbita o palpitaciones intensas, acude inmediatamente al servicio de Urgencias.
                </p>
              </Row>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3">
              <a href={waLink(`Hola Dr. González, me gustaría agendar cita en ${current.name}.`)} target="_blank" rel="noreferrer" data-testid="ubicacion-cta-whatsapp">
                <Button
                  size="lg"
                  className="w-full rounded-full bg-[hsl(var(--soft-red))] hover:bg-[hsl(var(--soft-red))]/90 text-white shadow-warm"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </Button>
              </a>
              <a href={telLink} data-testid="ubicacion-cta-call">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full rounded-full border-[hsl(var(--navy))] text-[hsl(var(--navy))] hover:bg-[hsl(var(--navy))] hover:text-[hsl(var(--cream))]"
                >
                  <Phone className="w-4 h-4" /> Llamar
                </Button>
              </a>
            </div>
          </div>

          {/* Map column */}
          <div className="lg:col-span-7">
            <div className="relative rounded-3xl overflow-hidden border border-[hsl(var(--border))] shadow-soft bg-white">
              <iframe
                key={current.id}
                title={`Ubicación ${current.name}`}
                src={mapSrc}
                width="100%"
                height="540"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="google-maps-iframe"
              />
              <a
                href={current.mapsLink}
                target="_blank"
                rel="noreferrer"
                className="absolute bottom-5 right-5 bg-white rounded-full px-4 py-2.5 text-sm font-medium text-[hsl(var(--navy))] shadow-soft hover:shadow-warm transition-all flex items-center gap-2"
                data-testid="open-in-maps-link"
              >
                <MapPin className="w-4 h-4 text-[hsl(var(--soft-red))]" />
                Abrir en Google Maps
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Row = ({ icon: Icon, title, children }) => (
  <div className="flex gap-4">
    <div className="shrink-0 w-10 h-10 rounded-xl bg-white border border-[hsl(var(--border))] grid place-items-center">
      <Icon className="w-4 h-4 text-[hsl(var(--soft-red))]" strokeWidth={1.8} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--navy-soft))] mb-1">
        {title}
      </div>
      {children}
    </div>
  </div>
);
